import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Bookmark, BookmarkCheck, X } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const PLAYER_IMG =
  "https://images.unsplash.com/photo-1759338780815-8dbc17f7ddf5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHx5b3VuZyUyMGZvb3RiYWxsJTIwcGxheWVyJTIwcG9ydHJhaXQlMjBkeW5hbWljJTIwc29jY2VyJTIwZmllbGR8ZW58MHx8fHwxNzg4NzE5NTc4fDA&ixlib=rb-4.1.0&q=85&w=1200&auto=format&fit=crop";

export default function Showcase({ t }) {
  const [modal, setModal] = useState(false);
  const [saved, setSaved] = useState(false);

  const stats = [
    { k: "RUOLO / POS", v: "ATT" },
    { k: "ETÀ / AGE", v: "17" },
    { k: "ALTEZZA / HT", v: "184 CM" },
    { k: "GOL", v: "14" },
    { k: "PRESENZE / APP", v: "22" },
    { k: "PIEDE / FOOT", v: "DX" },
  ];

  return (
    <section
      id="showcase"
      data-testid="showcase-section"
      className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36"
    >
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#00F562]">
        {t.showcase.overline}
      </p>
      <h2 className="mt-4 max-w-2xl font-display text-2xl font-extrabold uppercase tracking-tight sm:text-3xl lg:text-4xl">
        {t.showcase.title}
      </h2>
      <p className="mt-4 max-w-xl text-base text-slate-400">{t.showcase.body}</p>

      <motion.div
        data-testid="player-showcase-card"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="mt-14 grid grid-cols-1 overflow-hidden rounded-3xl border border-white/[0.08] bg-[#0D111A] lg:grid-cols-[0.9fr_1.1fr]"
      >
        <div className="relative min-h-[320px]">
          <img
            src={PLAYER_IMG}
            alt="Marco Rossi"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0D111A]/90 max-lg:bg-gradient-to-t" />
          <button
            data-testid="showcase-play-button"
            onClick={() => setModal(true)}
            className="group absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#00F562] text-[#06080C] shadow-[0_0_50px_rgba(0,245,98,0.5)] transition-transform duration-300 hover:scale-110"
          >
            <Play size={28} fill="currentColor" className="ml-1" />
          </button>
        </div>

        <div className="p-8 lg:p-12">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#00F562]">
                TH // VERIFIED
              </p>
              <h3 className="mt-2 font-display text-3xl font-black uppercase tracking-tight">
                Marco Rossi
              </h3>
              <p className="mt-1 text-sm text-slate-400">
                US Tor di Quinto — Roma
              </p>
            </div>
            <button
              data-testid="showcase-save-button"
              onClick={() => setSaved((s) => !s)}
              className={`flex items-center gap-2 rounded-full border px-4 py-2 text-[11px] font-bold uppercase tracking-widest transition-all duration-300 ${
                saved
                  ? "border-[#00F562] bg-[#00F562]/10 text-[#00F562]"
                  : "border-white/15 text-slate-300 hover:border-[#00F562]/50 hover:text-[#00F562]"
              }`}
            >
              {saved ? <BookmarkCheck size={14} /> : <Bookmark size={14} />}
              {saved ? t.showcase.saved : t.showcase.save}
            </button>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {stats.map((s) => (
              <div
                key={s.k}
                data-testid={`showcase-stat-${s.k.split(" ")[0].toLowerCase()}`}
                className="rounded-xl border border-white/[0.08] bg-[#06080C] p-4"
              >
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">
                  {s.k}
                </p>
                <p className="mt-1 font-mono text-lg font-bold text-white">
                  {s.v}
                </p>
              </div>
            ))}
          </div>

          <button
            data-testid="showcase-watch-button"
            onClick={() => setModal(true)}
            className="mt-8 flex items-center gap-3 rounded-full bg-[#00F562] px-6 py-3.5 text-xs font-extrabold uppercase tracking-widest text-[#06080C] transition-all duration-300 hover:shadow-[0_0_35px_rgba(0,245,98,0.5)]"
          >
            <Play size={14} fill="currentColor" />
            {t.showcase.watch}
          </button>
          <p className="mt-5 font-mono text-[10px] uppercase tracking-widest text-slate-600">
            {t.showcase.mockDisclaimer}
          </p>
        </div>
      </motion.div>

      <AnimatePresence>
        {modal && (
          <motion.div
            data-testid="highlights-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-[#06080C]/80 p-6 backdrop-blur-md"
            onClick={() => setModal(false)}
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0D111A] p-8 text-center"
            >
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#00F562]/15 text-[#00F562]">
                <Play size={26} fill="currentColor" />
              </span>
              <h4 className="mt-5 font-display text-xl font-bold uppercase">
                {t.showcase.modalTitle}
              </h4>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">
                {t.showcase.modalBody}
              </p>
              <button
                data-testid="highlights-modal-close"
                onClick={() => setModal(false)}
                className="mt-7 inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-xs font-bold uppercase tracking-widest text-slate-200 transition-colors duration-300 hover:border-[#00F562]/60 hover:text-[#00F562]"
              >
                <X size={14} />
                {t.showcase.close}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
